import Layout from 'layout';
import { lazy, Suspense } from 'react';
import { Navigate, Route, Routes } from 'react-router-dom';
import { ROUTE_PATH } from 'utils/route-util';
import { useAuthContext } from 'context/AuthContext';
import { Stack } from '@mui/material';
import { CusLoading } from 'components/CusLoading';

// Parent Page

// -- login --
const Login = lazy(() => import('pages/Login'));

// -- dashboard --
const Dashboard = lazy(() => import('pages/Dashboard'));

// // -- order --
const Orders = lazy(() => import('pages/Orders'));
const Quotation = lazy(() => import('pages/Orders/pages/Quotations'));
const Payments = lazy(() => import('pages/Orders/pages/Payment'));
const QuotationForm = lazy(() => import('pages/Orders/pages/QuotationForm'));
const UpdatePayments = lazy(() => import('pages/Orders/pages/UpdatePayment'));
const KhqrPage = lazy(() => import('pages/Orders/pages/Khqr'));
const PreviewQuotation = lazy(
  () => import('pages/Orders/pages/PreviewQuotation')
);
const FinalInvoiceForm = lazy(
  () => import('pages/Orders/pages/FinalInvoiceForm')
);
const PreviewOrder = lazy(() => import('pages/Orders/pages/PreviewOrder'));
const OrderForm = lazy(() => import('pages/Orders/pages/OrderForm'));

// // -- expense --
const Expense = lazy(() => import('pages/Expenses'));
const ExpenseForm = lazy(() => import('pages/Expenses/pages/ExpenseForm'));
const ExpensePreview = lazy(
  () => import('pages/Expenses/pages/ExpenseInvoicePreview')
);

// -- customer --
const CustomerPage = lazy(() => import('pages/Participants'));
const CustomerForm = lazy(
  () => import('pages/Participants/pages/NewCustomerForm')
);

// -- potential --
const PotentialPage = lazy(() => import('pages/Participants/pages/Potential'));
const PotentialForm = lazy(
  () => import('pages/Participants/pages/Potential/PotentialForm')
);

const SupplierPage = lazy(() => import('pages/Participants/pages/Supplier'));
const SupplierForm = lazy(
  () => import('pages/Participants/pages/Supplier/SupplierForm')
);
// // -- notification --
const Notifications = lazy(() => import('pages/Reminder'));

// // -- menu --
const Menus = lazy(() => import('pages/Menus'));
const MenuForm = lazy(() => import('pages/Menus/pages/MenuForm'));
const FoodForm = lazy(() => import('pages/Menus/pages/FoodForm'));
const FoodPage = lazy(() => import('pages/Menus/pages/FoodPage'));

// // -- prepare grocery --
// const PrepareGrocery = lazy(() => import('pages/PrepareGrocery'));
// const PrepareGroceryForm = lazy(
//   () => import('pages/PrepareGrocery/pages/PrepareGroceryForm')
// );
// const PreviewPrepareGrocery = lazy(
//   () => import('pages/PrepareGrocery/pages/PrepareGroceryPreview')
// );

// -- inventory --
const Inventories = lazy(() => import('pages/Inventories'));
const InventoryUnit = lazy(() => import('pages/Inventories/pages/Units'));
const InventoryCategory = lazy(
  () => import('pages/Inventories/pages/Categories')
);
const InventoryReport = lazy(() => import('pages/Inventories/pages/Reports'));
const InventoryForm = lazy(
  () => import('pages/Inventories/pages/InventoryForm')
);

// -- users --
const Users = lazy(() => import('pages/Users'));
const Roles = lazy(() => import('pages/Users/pages/Roles'));
const Permission = lazy(() => import('pages/Users/pages/Permissions'));
const NewRoleForm = lazy(() => import('pages/Users/pages/Roles/NewRoleForm'));
const NewUserForm = lazy(() => import('pages/Users/pages/NewUserForm'));

// // -- setting --
const CompanyProfile = lazy(() => import('pages/Setting'));

export default function AllRoutes() {
  const { authState } = useAuthContext();

  return (
    <Routes>
      <Route
        path={ROUTE_PATH.root}
        element={
          authState.authed ? <Layout /> : <Navigate to={ROUTE_PATH.login} />
        }
      >
        {/* Dashboard */}
        <Route
          path={ROUTE_PATH.dashboard}
          element={
            <Suspense
              fallback={
                <Stack
                  height='80vh'
                  alignItems='center'
                  justifyContent='center'
                >
                  <CusLoading />
                </Stack>
              }
            >
              <Dashboard />
            </Suspense>
          }
        />

        {/* Orders */}
        <Route
          path={ROUTE_PATH.orders.order}
          element={
            <Suspense
              fallback={
                <Stack
                  height='80vh'
                  alignItems='center'
                  justifyContent='center'
                >
                  <CusLoading />
                </Stack>
              }
            >
              <Orders />
            </Suspense>
          }
        />
        <Route
          path={ROUTE_PATH.orders.createOrder}
          element={
            <Suspense
              fallback={
                <Stack
                  height='80vh'
                  alignItems='center'
                  justifyContent='center'
                >
                  <CusLoading />
                </Stack>
              }
            >
              <OrderForm />
            </Suspense>
          }
        />
        <Route
          path={ROUTE_PATH.orders.updateBooking}
          element={
            <Suspense
              fallback={
                <Stack
                  height='80vh'
                  alignItems='center'
                  justifyContent='center'
                >
                  <CusLoading />
                </Stack>
              }
            >
              <OrderForm />
            </Suspense>
          }
        />
        <Route
          path={ROUTE_PATH.orders.updateFinal}
          element={
            <Suspense
              fallback={
                <Stack
                  height='80vh'
                  alignItems='center'
                  justifyContent='center'
                >
                  <CusLoading />
                </Stack>
              }
            >
              <FinalInvoiceForm />
            </Suspense>
          }
        />
        <Route
          path={ROUTE_PATH.orders.createQuotation}
          element={
            <Suspense
              fallback={
                <Stack
                  height='80vh'
                  alignItems='center'
                  justifyContent='center'
                >
                  <CusLoading />
                </Stack>
              }
            >
              <QuotationForm />
            </Suspense>
          }
        />
        <Route
          path={ROUTE_PATH.orders.updateQuotation}
          element={
            <Suspense
              fallback={
                <Stack
                  height='80vh'
                  alignItems='center'
                  justifyContent='center'
                >
                  <CusLoading />
                </Stack>
              }
            >
              <QuotationForm />
            </Suspense>
          }
        />
        <Route
          path={ROUTE_PATH.orders.previewQuotation}
          element={
            <Suspense
              fallback={
                <Stack
                  height='80vh'
                  alignItems='center'
                  justifyContent='center'
                >
                  <CusLoading />
                </Stack>
              }
            >
              <PreviewQuotation />
            </Suspense>
          }
        />
        <Route
          path={ROUTE_PATH.orders.previewInvoice}
          element={
            <Suspense
              fallback={
                <Stack
                  height='80vh'
                  alignItems='center'
                  justifyContent='center'
                >
                  <CusLoading />
                </Stack>
              }
            >
              <PreviewOrder />
            </Suspense>
          }
        />
        <Route
          path={ROUTE_PATH.orders.quotations}
          element={
            <Suspense
              fallback={
                <Stack
                  height='80vh'
                  alignItems='center'
                  justifyContent='center'
                >
                  <CusLoading />
                </Stack>
              }
            >
              <Quotation />
            </Suspense>
          }
        />
        <Route
          path={ROUTE_PATH.orders.payments}
          element={
            <Suspense
              fallback={
                <Stack
                  height='80vh'
                  alignItems='center'
                  justifyContent='center'
                >
                  <CusLoading />
                </Stack>
              }
            >
              <Payments />
            </Suspense>
          }
        />
        <Route
          path={ROUTE_PATH.orders.updatePayment}
          element={
            <Suspense
              fallback={
                <Stack
                  height='80vh'
                  alignItems='center'
                  justifyContent='center'
                >
                  <CusLoading />
                </Stack>
              }
            >
              <UpdatePayments />
            </Suspense>
          }
        />

        {/* Expenses */}
        <Route
          path={ROUTE_PATH.orders.invoices}
          element={
            <Suspense
              fallback={
                <Stack
                  height='80vh'
                  alignItems='center'
                  justifyContent='center'
                >
                  <CusLoading />
                </Stack>
              }
            >
              <Expense />
            </Suspense>
          }
        />
        <Route
          path={ROUTE_PATH.orders.updateExpense}
          element={
            <Suspense
              fallback={
                <Stack
                  height='80vh'
                  alignItems='center'
                  justifyContent='center'
                >
                  <CusLoading />
                </Stack>
              }
            >
              <ExpenseForm />
            </Suspense>
          }
        />
        <Route
          path={ROUTE_PATH.orders.expensePreview}
          element={
            <Suspense
              fallback={
                <Stack
                  height='80vh'
                  alignItems='center'
                  justifyContent='center'
                >
                  <CusLoading />
                </Stack>
              }
            >
              <ExpensePreview />
            </Suspense>
          }
        />

        {/* Customers */}
        <Route
          path={ROUTE_PATH.participants.customer}
          element={
            <Suspense
              fallback={
                <Stack
                  height='80vh'
                  alignItems='center'
                  justifyContent='center'
                >
                  <CusLoading />
                </Stack>
              }
            >
              <CustomerPage />
            </Suspense>
          }
        />
        <Route
          path={ROUTE_PATH.participants.createCustomer}
          element={
            <Suspense
              fallback={
                <Stack
                  height='80vh'
                  alignItems='center'
                  justifyContent='center'
                >
                  <CusLoading />
                </Stack>
              }
            >
              <CustomerForm />
            </Suspense>
          }
        />
        <Route
          path={ROUTE_PATH.participants.updateCustomer}
          element={
            <Suspense
              fallback={
                <Stack
                  height='80vh'
                  alignItems='center'
                  justifyContent='center'
                >
                  <CusLoading />
                </Stack>
              }
            >
              <CustomerForm />
            </Suspense>
          }
        />
        <Route
          path={ROUTE_PATH.participants.potential}
          element={
            <Suspense
              fallback={
                <Stack
                  height='80vh'
                  alignItems='center'
                  justifyContent='center'
                >
                  <CusLoading />
                </Stack>
              }
            >
              <PotentialPage />
            </Suspense>
          }
        />
        <Route
          path={ROUTE_PATH.participants.createPotential}
          element={
            <Suspense
              fallback={
                <Stack
                  height='80vh'
                  alignItems='center'
                  justifyContent='center'
                >
                  <CusLoading />
                </Stack>
              }
            >
              <PotentialForm />
            </Suspense>
          }
        />
        <Route
          path={ROUTE_PATH.participants.updatePotential}
          element={
            <Suspense
              fallback={
                <Stack
                  height='80vh'
                  alignItems='center'
                  justifyContent='center'
                >
                  <CusLoading />
                </Stack>
              }
            >
              <PotentialForm />
            </Suspense>
          }
        />

        <Route
          path={ROUTE_PATH.participants.supplier}
          element={
            <Suspense
              fallback={
                <Stack
                  height='80vh'
                  alignItems='center'
                  justifyContent='center'
                >
                  <CusLoading />
                </Stack>
              }
            >
              <SupplierPage />
            </Suspense>
          }
        />
        <Route
          path={ROUTE_PATH.participants.createSupplier}
          element={
            <Suspense
              fallback={
                <Stack
                  height='80vh'
                  alignItems='center'
                  justifyContent='center'
                >
                  <CusLoading />
                </Stack>
              }
            >
              <SupplierForm />
            </Suspense>
          }
        />
        <Route
          path={ROUTE_PATH.participants.updateSupplier}
          element={
            <Suspense
              fallback={
                <Stack
                  height='80vh'
                  alignItems='center'
                  justifyContent='center'
                >
                  <CusLoading />
                </Stack>
              }
            >
              <SupplierForm />
            </Suspense>
          }
        />

        {/* Notification */}
        <Route
          path={ROUTE_PATH.notification.root}
          element={
            <Suspense
              fallback={
                <Stack
                  height='80vh'
                  alignItems='center'
                  justifyContent='center'
                >
                  <CusLoading />
                </Stack>
              }
            >
              <Notifications />
            </Suspense>
          }
        />

        {/* Menus */}
        <Route
          path={ROUTE_PATH.menus.package}
          element={
            <Suspense
              fallback={
                <Stack
                  height='80vh'
                  alignItems='center'
                  justifyContent='center'
                >
                  <CusLoading />
                </Stack>
              }
            >
              <Menus />
            </Suspense>
          }
        />
        <Route
          path={ROUTE_PATH.menus.createMenuPackage}
          element={
            <Suspense
              fallback={
                <Stack
                  height='80vh'
                  alignItems='center'
                  justifyContent='center'
                >
                  <CusLoading />
                </Stack>
              }
            >
              <MenuForm />
            </Suspense>
          }
        />
        <Route
          path={ROUTE_PATH.menus.updateMenuPackage}
          element={
            <Suspense
              fallback={
                <Stack
                  height='80vh'
                  alignItems='center'
                  justifyContent='center'
                >
                  <CusLoading />
                </Stack>
              }
            >
              <MenuForm />
            </Suspense>
          }
        />
        <Route
          path={ROUTE_PATH.menus.food}
          element={
            <Suspense
              fallback={
                <Stack
                  height='80vh'
                  alignItems='center'
                  justifyContent='center'
                >
                  <CusLoading />
                </Stack>
              }
            >
              <FoodPage />
            </Suspense>
          }
        />
        <Route
          path={ROUTE_PATH.menus.createFood}
          element={
            <Suspense
              fallback={
                <Stack
                  height='80vh'
                  alignItems='center'
                  justifyContent='center'
                >
                  <CusLoading />
                </Stack>
              }
            >
              <FoodForm />
            </Suspense>
          }
        />
        <Route
          path={ROUTE_PATH.menus.updateFood}
          element={
            <Suspense
              fallback={
                <Stack
                  height='80vh'
                  alignItems='center'
                  justifyContent='center'
                >
                  <CusLoading />
                </Stack>
              }
            >
              <FoodForm />
            </Suspense>
          }
        />

        {/* Prepare Grocery */}
        {/* <Route
          path={ROUTE_PATH.prepareGrocery.root}
          element={
            <Suspense fallback={<Stack height='80vh' alignItems='center' justifyContent='center'>
        <CusLoading />
      </Stack>}>
              <PrepareGrocery />
            </Suspense>
          }
        />
        <Route
          path={ROUTE_PATH.prepareGrocery.createGrocery}
          element={
            <Suspense fallback={<Stack height='80vh' alignItems='center' justifyContent='center'>
        <CusLoading />
      </Stack>}>
              <PrepareGroceryForm />
            </Suspense>
          }
        />
        <Route
          path={ROUTE_PATH.prepareGrocery.updateGrocery}
          element={
            <Suspense fallback={<Stack height='80vh' alignItems='center' justifyContent='center'>
        <CusLoading />
      </Stack>}>
              <PrepareGroceryForm />
            </Suspense>
          }
        />
        <Route
          path={ROUTE_PATH.prepareGrocery.previewGrocery}
          element={
            <Suspense fallback={<Stack height='80vh' alignItems='center' justifyContent='center'>
        <CusLoading />
      </Stack>}>
              <PreviewPrepareGrocery />
            </Suspense>
          }
        /> */}

        {/* Inventory */}
        <Route
          path={ROUTE_PATH.inventories.product}
          element={
            <Suspense
              fallback={
                <Stack
                  height='80vh'
                  alignItems='center'
                  justifyContent='center'
                >
                  <CusLoading />
                </Stack>
              }
            >
              <Inventories />
            </Suspense>
          }
        />
        <Route
          path={ROUTE_PATH.inventories.addInventory}
          element={
            <Suspense
              fallback={
                <Stack
                  height='80vh'
                  alignItems='center'
                  justifyContent='center'
                >
                  <CusLoading />
                </Stack>
              }
            >
              <InventoryForm />
            </Suspense>
          }
        />
        <Route
          path={ROUTE_PATH.inventories.units}
          element={
            <Suspense
              fallback={
                <Stack
                  height='80vh'
                  alignItems='center'
                  justifyContent='center'
                >
                  <CusLoading />
                </Stack>
              }
            >
              <InventoryUnit />
            </Suspense>
          }
        />
        <Route
          path={ROUTE_PATH.inventories.categories}
          element={
            <Suspense
              fallback={
                <Stack
                  height='80vh'
                  alignItems='center'
                  justifyContent='center'
                >
                  <CusLoading />
                </Stack>
              }
            >
              <InventoryCategory />
            </Suspense>
          }
        />
        <Route
          path={ROUTE_PATH.inventories.inventoryReport}
          element={
            <Suspense
              fallback={
                <Stack
                  height='80vh'
                  alignItems='center'
                  justifyContent='center'
                >
                  <CusLoading />
                </Stack>
              }
            >
              <InventoryReport />
            </Suspense>
          }
        />

        {/* Users */}
        <Route
          path={ROUTE_PATH.users.root}
          element={
            <Suspense
              fallback={
                <Stack
                  height='80vh'
                  alignItems='center'
                  justifyContent='center'
                >
                  <CusLoading />
                </Stack>
              }
            >
              <Users />
            </Suspense>
          }
        />
        <Route
          path={ROUTE_PATH.users.createNewUser}
          element={
            <Suspense
              fallback={
                <Stack
                  height='80vh'
                  alignItems='center'
                  justifyContent='center'
                >
                  <CusLoading />
                </Stack>
              }
            >
              <NewUserForm />
            </Suspense>
          }
        />
        <Route
          path={ROUTE_PATH.users.updateUser}
          element={
            <Suspense
              fallback={
                <Stack
                  height='80vh'
                  alignItems='center'
                  justifyContent='center'
                >
                  <CusLoading />
                </Stack>
              }
            >
              <NewUserForm />
            </Suspense>
          }
        />
        <Route
          path={ROUTE_PATH.users.roles}
          element={
            <Suspense
              fallback={
                <Stack
                  height='80vh'
                  alignItems='center'
                  justifyContent='center'
                >
                  <CusLoading />
                </Stack>
              }
            >
              <Roles />
            </Suspense>
          }
        />

        <Route
          path={ROUTE_PATH.users.createNewRole}
          element={
            <Suspense
              fallback={
                <Stack
                  height='80vh'
                  alignItems='center'
                  justifyContent='center'
                >
                  <CusLoading />
                </Stack>
              }
            >
              <NewRoleForm />
            </Suspense>
          }
        />
        <Route
          path={ROUTE_PATH.users.updateRole}
          element={
            <Suspense
              fallback={
                <Stack
                  height='80vh'
                  alignItems='center'
                  justifyContent='center'
                >
                  <CusLoading />
                </Stack>
              }
            >
              <NewRoleForm />
            </Suspense>
          }
        />

        <Route
          path={ROUTE_PATH.users.permission}
          element={
            <Suspense
              fallback={
                <Stack
                  height='80vh'
                  alignItems='center'
                  justifyContent='center'
                >
                  <CusLoading />
                </Stack>
              }
            >
              <Permission />
            </Suspense>
          }
        />

        {/* Setting */}
        <Route
          path={ROUTE_PATH.setting.root}
          element={
            <Suspense
              fallback={
                <Stack
                  height='80vh'
                  alignItems='center'
                  justifyContent='center'
                >
                  <CusLoading />
                </Stack>
              }
            >
              <CompanyProfile />
            </Suspense>
          }
        />

        {/* Root Page */}
        <Route
          path={ROUTE_PATH.root}
          element={<Navigate to={ROUTE_PATH.dashboard} />}
        />
        <Route path='*' element={<Navigate to={ROUTE_PATH.dashboard} />} />
      </Route>
      <Route
        path={ROUTE_PATH.login}
        element={
          authState.authed ? (
            <Navigate to={ROUTE_PATH.root} />
          ) : (
            <Suspense
              fallback={
                <Stack
                  height='80vh'
                  alignItems='center'
                  justifyContent='center'
                >
                  <CusLoading />
                </Stack>
              }
            >
              <Login />
            </Suspense>
          )
        }
      />
      <Route
        path={ROUTE_PATH.orders.khqr}
        element={
          <Suspense
            fallback={
              <Stack height='80vh' alignItems='center' justifyContent='center'>
                <CusLoading />
              </Stack>
            }
          >
            <KhqrPage />
          </Suspense>
        }
      />
    </Routes>
  );
}
